import { isRumActive, setRumUserFromAuth0Sub, startNewRumSession } from '@/rum';

import { type ReactNode, useCallback, useEffect } from 'react';

import { setLogoutFn, setTokenGetter } from '@app/baseApi';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetElevioEmailHashQuery } from '@features/tenants/api/tenantsApi';

import { initAnalytics, trackLogin } from '@/utils/analytics';
import { initElevio } from '@/utils/elevio';
import { initIntercom } from '@/utils/intercom';
import { logger } from '@/utils/logger';

interface AuthTokenProviderProps {
  children: ReactNode;
}

/**
 * Wait for the browser to come back online before attempting a network call.
 * Resolves immediately if already online.
 */
const waitForOnline = (): Promise<void> => {
  if (navigator.onLine) return Promise.resolve();
  return new Promise((resolve) => {
    const handler = () => {
      window.removeEventListener('online', handler);
      resolve();
    };
    window.addEventListener('online', handler);
  });
};

/**
 * Connects Auth0 to RTK Query by providing the token getter function
 * This must be rendered inside Auth0Provider and outside any RTK Query usage
 */
export const AuthTokenProvider = ({ children }: AuthTokenProviderProps) => {
  const { getAccessTokenSilently, logout, user, isAuthenticated } = useAuth0();

  // Get Elevio hash for authenticated user
  const { data: elevioHash } = useGetElevioEmailHashQuery(user?.email || '', {
    skip: !isAuthenticated || !user?.email,
  });

  const handleLogout = useCallback(() => {
    if (isRumActive()) {
      startNewRumSession();
    }
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Register logout function so baseApi can trigger it on 401 responses
  useEffect(() => {
    setLogoutFn(handleLogout);
  }, [handleLogout]);

  useEffect(() => {
    setTokenGetter(async () => {
      try {
        await waitForOnline();
        return await getAccessTokenSilently();
      } catch (err) {
        const isNetworkError =
          err instanceof TypeError &&
          (err.message === 'Failed to fetch' || err.message === 'Load failed');

        if (isNetworkError) {
          logger.warn('[Auth] Token refresh failed (network). Waiting for reconnect...');
          await waitForOnline();
          try {
            return await getAccessTokenSilently();
          } catch (_retryErr) {
            logger.warn('[Auth] Token refresh failed after reconnect');
            return undefined;
          }
        }

        if (isAuthenticated) {
          logger.warn('[Auth] Token refresh failed — logging out', err);
          handleLogout();
        }
        return undefined;
      }
    });
  }, [getAccessTokenSilently, isAuthenticated, handleLogout]);

  // Initialize Elevio help center when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.email && elevioHash) {
      initElevio(user.email, async () => elevioHash);
    }
  }, [isAuthenticated, user?.email, elevioHash]);

  // Initialize Intercom live chat when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.email && user?.name) {
      initIntercom(user.email, user.name);
    }
  }, [isAuthenticated, user?.email, user?.name]);

  // Initialize Google Analytics and track login event
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      initAnalytics();
      trackLogin(user.email, user.sub || '');
    }
  }, [isAuthenticated, user?.email, user?.sub]);

  // RUM userId must be UUID-shaped; derive stable id from Auth0 sub (no email/name)
  useEffect(() => {
    if (isAuthenticated && user?.sub && isRumActive()) {
      void setRumUserFromAuth0Sub(user.sub);
    }
  }, [isAuthenticated, user?.sub]);

  return children;
};
